(function () {
  'use strict';

  /**
   * tl-route-autoadd.js  (v4 — checkout intercept + Continue Without navigation)
   *
   * CHANGES IN v4 (Aug 30 2026):
   *   1. Checkout button intercept.
   *      When the shopper clicks Checkout (and has NOT opted out this session),
   *      the form submit is intercepted if a Route sync is still in flight or
   *      debouncing. The submit waits up to 4 seconds for syncRoute() to settle
   *      (Route is in the cart), then re-submits the form normally. This ensures
   *      Route is always present in the cart before checkout — no race condition
   *      where the drawer was just opened and the 350ms debounce hadn't fired yet.
   *      Opted-out shoppers are not intercepted: they already confirmed they want
   *      to proceed without Package Protection.
   *
   *   2. "Continue Without" now navigates to /checkout.
   *      Previously the handler removed Route and fired cart:change to refresh the
   *      drawer (drawer stayed open). Now it removes Route and immediately sends
   *      the shopper to /checkout — Route unchecked, as intended. The drawer
   *      refresh dispatch is gone; it's irrelevant when the page is changing.
   *
   * CHANGES IN v3 (Aug 30 2026):
   *   1. localStorage → sessionStorage for the opt-out flag.
   *   2. _fireCartChange() now fetches the cart and dispatches cart:change WITH
   *      a detail.cart payload.
   *   3. The cart:change listener no longer requires e.detail.cart to trigger a sync.
   *   4. The "Continue Without" opt-out handler now also fetches the cart and
   *      dispatches cart:change with detail so the drawer refreshes immediately
   *      after Route is removed.
   */

  // ─── Route variant price tiers ────────────────────────────────────────────
  var ROUTE_TIERS = [
    [98,40930778873950],[115,40930778906718],[135,40930778939486],
    [155,40930778972254],[175,40930779005022],[195,40930779037790],
    [215,40930779070558],[235,40930779103326],[255,40930779136094],
    [275,40930779168862],[295,40930779201630],[315,40930779234398],
    [335,40930779267166],[355,40930779299934],[375,40930779332702],
    [395,40930779365470],[415,40930779398238],[435,40930779431006],
    [455,40930779463774],[475,40930779496542],[495,40930779529310],
    [515,40930779562078],[535,40930779594846],[555,40930779627614],
    [575,40930779660382],[595,40930779693150],[615,40930779725918],
    [635,40930779758686],[655,40930779791454],[675,40930779824222],
    [695,40930779856990],[715,40930779889758],[735,40930779922526],
    [755,40930779955294],[775,40930779988062],[795,40930780020830],
    [815,40930780053598],[835,40930780086366],[855,40930780119134],
    [875,40930780151902],[895,40930780184670],[938,40930780217438],
    [1003,40930780250206],[1068,40930780282974],[1133,40930780315742],
    [1198,40930780348510],[1263,40930780381278],[1328,40930780414046],
    [1393,40930780446814],[1458,40930780479582],[1523,40930780512350],
    [1588,40930780545118],[1653,40930780577886],[1718,40930780610654],
    [1783,40930780643422],[1848,40930780676190],[1913,40930780708958],
    [1978,40930780741726],[2043,40930780774494],[2438,40930780807262],
    [3163,40930780840030],[3888,40930780872798],[4613,40930780905566],
    [5338,40930780938334],[6063,40930780971102],[6788,40930781003870],
    [7513,40930781036638],[8238,40930781069406],[8963,40930781102174],
    [9688,40930781134942],[10413,40930781167710],[11138,40930781200478],
    [11863,40930781233246],[12588,40930781266014],[13313,40930781298782],
    [14038,40930781331550]
  ];

  // ─── Pick the right variant for a given subtotal ──────────────────────────
  function getVariantForSubtotal(subtotalCents) {
    var target = Math.max(98, Math.round(subtotalCents * 0.015));
    var best = ROUTE_TIERS[0][1];
    for (var i = 0; i < ROUTE_TIERS.length; i++) {
      if (ROUTE_TIERS[i][0] <= target) { best = ROUTE_TIERS[i][1]; }
      else { break; }
    }
    return best;
  }

  // ─── State flags ──────────────────────────────────────────────────────────
  var _updateInFlight    = false;
  var _cooldown          = false;
  var _blockSectionRenders = false;
  var _pendingSync       = false;
  var _debounceTimer     = null;

  // ─── Fire cart:change with the live cart so Prestige re-renders the drawer ─
  async function _fireCartChange() {
    _cooldown = true;
    try {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); });
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { cart: cart } })
      );
    } catch (err) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true })
      );
    }
    setTimeout(function () {
      _cooldown = false;
      if (_pendingSync) { _pendingSync = false; scheduleSyncRoute(); }
    }, 1800);
  }

  // ─── Core sync function ───────────────────────────────────────────────────
  async function syncRoute() {
    if (_updateInFlight || _cooldown) return;

    if (sessionStorage.getItem('tl_route_opted_out')) {
      _updateInFlight = true;
      try {
        var optCart = await fetch('/cart.js').then(function (r) { return r.json(); });
        var sneakyRoute = optCart.items.find(function (item) {
          return item.product_type === 'routeins' || (item.handle && item.handle.indexOf('routeins') !== -1);
        });
        if (sneakyRoute) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: sneakyRoute.key, quantity: 0 })
          });
        }
      } catch (err) {
        console.warn('[TL Route] opt-out cleanup error', err);
      } finally {
        _updateInFlight = false;
      }
      return;
    }

    _updateInFlight = true;
    try {
      var cart = await fetch('/cart.js').then(function (r) { return r.json(); });

      var routeItem = null;
      var nonRouteSubtotal = 0;

      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.handle === 'routeins') {
          routeItem = item;
        } else {
          nonRouteSubtotal += item.original_line_price;
        }
      }

      if (nonRouteSubtotal === 0) {
        if (routeItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: routeItem.key, quantity: 0 })
          });
          _fireCartChange();
        }
        return;
      }

      var neededVariantId = getVariantForSubtotal(nonRouteSubtotal);

      if (routeItem && routeItem.variant_id === neededVariantId) return;

      if (routeItem) {
        _blockSectionRenders = true;
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: routeItem.key, quantity: 0 })
        });
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: neededVariantId, quantity: 1 })
      });

      _blockSectionRenders = false;
      _fireCartChange();

    } catch (err) {
      // Silent fail — Route is non-critical
    } finally {
      _updateInFlight = false;
    }
  }

  // Debounced entry point
  function scheduleSyncRoute() {
    if (_updateInFlight) return;
    if (_cooldown) { _pendingSync = true; return; }
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(function () {
      _debounceTimer = null;
      syncRoute();
    }, 350);
  }

  // ─── Listeners ────────────────────────────────────────────────────────────

  document.addEventListener('cart:change', function (e) {
    if (_blockSectionRenders && !e.detail) { e.stopImmediatePropagation(); }
  }, true);

  document.addEventListener('cart:change', function () {
    scheduleSyncRoute();
  });

  var _drawerObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.attributeName === 'open' || m.attributeName === 'aria-hidden' || m.attributeName === 'class') {
        var el = m.target;
        if (!el.tagName || el.tagName.toLowerCase() !== 'cart-drawer') continue;
        var isOpen = el.hasAttribute('open') || el.getAttribute('aria-hidden') === 'false' || (el.classList && el.classList.contains('is-open'));
        if (isOpen) { scheduleSyncRoute(); break; }
      }
    }
  });

  function attachDrawerObserver() {
    var target = document.body || document.documentElement;
    _drawerObserver.observe(target, { attributes: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachDrawerObserver);
  } else {
    attachDrawerObserver();
  }

  // ─── Checkout button intercept (v4) ───────────────────────────────────────
  //
  // Ensures Route is fully synced into the cart before the form submits.
  // Only fires when a sync is actually pending or in-flight — if nothing
  // is pending, Route is already settled and we let the form through.
  document.addEventListener('submit', function (e) {
    var sub = e.submitter;
    if (!sub || sub.name !== 'checkout') return;
    if (sessionStorage.getItem('tl_route_opted_out')) return;
    if (!_updateInFlight && !_cooldown && !_debounceTimer) return;

    e.preventDefault();
    var form = e.target;
    var btn = sub;

    if (_debounceTimer && !_updateInFlight) {
      clearTimeout(_debounceTimer);
      _debounceTimer = null;
      syncRoute();
    }

    var waited = 0;
    var poll = setInterval(function () {
      waited += 100;
      if ((!_updateInFlight && !_cooldown) || waited >= 4000) {
        clearInterval(poll);
        if (form.requestSubmit) {
          form.requestSubmit(btn);
        } else {
          btn.click();
        }
      }
    }, 100);
  }, true);

  // ─── "Continue Without" opt-out handler (v4) ─────────────────────────────
  //
  // v4: removes Route then navigates to /checkout — shopper lands at checkout
  // without Package Protection. The sessionStorage flag stays set so that if
  // they use the browser back button, Route is not re-added.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tl-route-remove]');
    if (!btn) return;

    sessionStorage.setItem('tl_route_opted_out', '1');

    (async function () {
      try {
        var key = btn.getAttribute('data-tl-route-remove');
        if (!key) {
          var cartData = await fetch('/cart.js').then(function (r) { return r.json(); });
          var routeInCart = cartData.items.find(function (item) {
            return item.product_type === 'routeins' || (item.handle && item.handle.indexOf('routeins') !== -1);
          });
          if (routeInCart) key = routeInCart.key;
        }
        if (key) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: 0 })
          });
        }
        // v4: navigate to checkout with Route removed
        window.location.href = '/checkout';
      } catch (err) {
        console.warn('[TL Route] opt-out removal error', err);
        // Fallback: send to checkout even if removal failed; syncRoute's opt-out
        // branch will clean up Route on any subsequent cart:change.
        window.location.href = '/checkout';
      }
    })();
  });

})();
